import React, { useMemo, useState } from 'react';
import {
  IonBadge,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSearchbar,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { useRecipes } from '../hooks/useRecipes';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { formatCurrency, formatDecimal } from '../utils/format';
import { IProcessedMaterial, IRawMaterial, IRecipe, IMenuItem } from 'shared';
import MetricCard from '../components/MetricCard';

const Recipes: React.FC = () => {
  const history = useHistory();
  const { t, language } = useLanguage();
  const locale = language === 'vi' ? 'vi-VN' : 'en-US';
  const { recipes, menuItems, processedMaterials, rawMaterials, isLoading } = useRecipes();
  const [search, setSearch] = useState('');

  const menuMap = useMemo(() => {
    const map = new Map<string, IMenuItem>();
    menuItems.forEach((item) => {
      map.set(item.id, item);
    });
    return map;
  }, [menuItems]);

  const materialMap = useMemo(() => {
    const map = new Map<string, IProcessedMaterial | IRawMaterial>();
    processedMaterials.forEach((item: IProcessedMaterial) => map.set(item.id, item));
    rawMaterials.forEach((item: IRawMaterial) => map.set(item.id, item));
    return map;
  }, [processedMaterials, rawMaterials]);

  type RecipeGroup = { menuItem: IMenuItem; recipes: IRecipe[] };

  const groupedByMenuItem = useMemo(() => {
    const grouped = new Map<string, RecipeGroup>();
    recipes.forEach((recipe) => {
      const menuItem = menuMap.get(recipe.menuItemId);
      if (!menuItem) return;
      const entry = grouped.get(recipe.menuItemId) || { menuItem, recipes: [] };
      entry.recipes.push(recipe);
      grouped.set(recipe.menuItemId, entry);
    });
    return Array.from(grouped.values());
  }, [recipes, menuMap]);

  const filtered = groupedByMenuItem.filter(({ menuItem }) =>
    menuItem.product?.name?.toLowerCase().includes(search.toLowerCase()) ?? false,
  );

  const computeCost = (entry: RecipeGroup) => {
    return entry.recipes.reduce((total, recipe) => {
      const material = materialMap.get(recipe.processedMaterialId || recipe.rawMaterialId || '');
      if (!material) return total;
      const unitCost = Number(material.costPerUnit || 0);
      return total + unitCost * Number(recipe.quantityNeeded || 0);
    }, 0);
  };

  const analytics = useMemo(() => {
    return groupedByMenuItem.map((entry) => {
      const menu = entry.menuItem;
      const cost = computeCost(entry);
      const price = Number(menu.price || 0);
      const profit = price - cost;
      const margin = price > 0 ? (profit / price) * 100 : 0;
      return {
        id: menu.id,
        name: menu.product?.name || t('unknown'),
        price,
        cost,
        profit,
        margin,
      };
    });
  }, [groupedByMenuItem, t]);

  const profitabilityMetrics = useMemo(() => {
    if (analytics.length === 0) {
      return {
        totalRecipes: recipes.length,
        trackedProducts: 0,
        averageMargin: 0,
        averageCost: 0,
        totalProfit: 0,
      };
    }
    const trackedProducts = analytics.length;
    const totals = analytics.reduce(
      (acc, item) => {
        acc.margin += item.margin;
        acc.cost += item.cost;
        acc.profit += item.profit;
        return acc;
      },
      { margin: 0, cost: 0, profit: 0 },
    );

    return {
      totalRecipes: recipes.length,
      trackedProducts,
      averageMargin: trackedProducts ? totals.margin / trackedProducts : 0,
      averageCost: trackedProducts ? totals.cost / trackedProducts : 0,
      totalProfit: totals.profit,
    };
  }, [analytics, recipes.length]);

  const topProfitable = useMemo(
    () => [...analytics].sort((a, b) => b.margin - a.margin).slice(0, 5),
    [analytics],
  );

  const bottomProfitable = useMemo(
    () => [...analytics].sort((a, b) => a.margin - b.margin).slice(0, 5),
    [analytics],
  );

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonTitle>{t('recipes')}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
            <IonSpinner />
          </div>
        ) : (
          <>
            <section style={{ marginBottom: 16 }}>
              <MetricCard
                title={t('recipesWithCost') ?? 'Tracked recipes'}
                value={`${profitabilityMetrics.trackedProducts}`}
                context={`${t('totalRecipes') ?? 'Total recipes'}: ${profitabilityMetrics.totalRecipes}`}
                accent="primary"
              />
              <MetricCard
                title={t('avgMargin') ?? 'Average margin'}
                value={`${formatDecimal(profitabilityMetrics.averageMargin, {
                  locale,
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                })}%`}
                context={`${t('avgCostLabel') ?? 'Average cost'}: ${formatCurrency(profitabilityMetrics.averageCost, locale)}`}
                accent="success"
              />
              <MetricCard
                title={t('totalProfit') ?? 'Total profit'}
                value={formatCurrency(profitabilityMetrics.totalProfit, locale)}
                context={t('profitabilitySummary') ?? 'Aggregated profit across products'}
                accent="warning"
              />
            </section>

            <section style={{ marginBottom: 16 }}>
              <IonText color="primary">
                <h2 style={{ marginBottom: 12 }}>{t('bestPerformingRecipes') ?? 'Top profit recipes'}</h2>
              </IonText>
              {topProfitable.length === 0 ? (
                <IonText color="medium">
                  <p>{t('noProfitabilityData') ?? 'No profitability data yet.'}</p>
                </IonText>
              ) : (
                <IonList>
                  {topProfitable.map((item) => (
                    <IonItem key={`top-${item.id}`}>
                      <IonLabel>
                        <h3 style={{ fontWeight: 600 }}>{item.name}</h3>
                        <p>
                          {t('profitMargin') || 'Profit Margin'}:{' '}
                          {formatDecimal(item.margin, {
                            locale,
                            maximumFractionDigits: 1,
                            minimumFractionDigits: 1,
                          })}% • {t('totalRecipeCost') || 'Total Recipe Cost'}:{' '}
                          {formatCurrency(item.cost, locale)}
                        </p>
                      </IonLabel>
                      <IonBadge slot="end" color="success">
                        {formatCurrency(item.profit, locale)}
                      </IonBadge>
                    </IonItem>
                  ))}
                </IonList>
              )}
            </section>

            <section style={{ marginBottom: 16 }}>
              <IonText color="primary">
                <h2 style={{ marginBottom: 12 }}>{t('leastPerformingRecipes') ?? 'Lowest profit recipes'}</h2>
              </IonText>
              {bottomProfitable.length === 0 ? (
                <IonText color="medium">
                  <p>{t('noProfitabilityData') ?? 'No profitability data yet.'}</p>
                </IonText>
              ) : (
                <IonList>
                  {bottomProfitable.map((item) => (
                    <IonItem key={`bottom-${item.id}`}>
                      <IonLabel>
                        <h3 style={{ fontWeight: 600 }}>{item.name}</h3>
                        <p>
                          {t('profitMargin') || 'Profit Margin'}:{' '}
                          {formatDecimal(item.margin, {
                            locale,
                            maximumFractionDigits: 1,
                            minimumFractionDigits: 1,
                          })}% • {t('totalRecipeCost') || 'Total Recipe Cost'}:{' '}
                          {formatCurrency(item.cost, locale)}
                        </p>
                      </IonLabel>
                      <IonBadge slot="end" color={item.profit >= 0 ? 'warning' : 'danger'}>
                        {formatCurrency(item.profit, locale)}
                      </IonBadge>
                    </IonItem>
                  ))}
                </IonList>
              )}
            </section>

            <IonSearchbar
              value={search}
              onIonChange={(event) => setSearch(event.detail.value ?? '')}
              placeholder={t('searchProducts') ?? 'Search menu items'}
            />

            {filtered.length === 0 ? (
              <IonText color="medium">
                <p>{t('noData')}</p>
              </IonText>
            ) : (
              <IonList>
                {filtered.map((entry) => {
                  const menu = entry.menuItem;
                  const cost = computeCost(entry);
                  const price = Number(menu.price || 0);
                  const profit = price - cost;
                  const margin = price > 0 ? (profit / price) * 100 : 0;

                  return (
                    <IonItem
                      key={menu.id}
                      button
                      detail
                      onClick={() => history.push(`/tabs/recipes/${menu.id}`)}
                    >
                      <IonLabel>
                        <h2 style={{ fontWeight: 600 }}>{menu.product?.name || t('unknown')}</h2>
                        <p>
                          {t('totalRecipeCost') || 'Total Recipe Cost'}:{' '}
                          {formatCurrency(cost, locale)} • {t('sellingPrice') || 'Price'}:{' '}
                          {formatCurrency(price, locale)}
                        </p>
                        <p>
                          {t('profitMargin') || 'Profit Margin'}:{' '}
                          {formatDecimal(margin, {
                            locale,
                            maximumFractionDigits: 1,
                            minimumFractionDigits: 1,
                          })}%
                        </p>
                      </IonLabel>
                      <IonBadge slot="end" color={profit >= 0 ? 'success' : 'warning'}>
                        {formatCurrency(profit, locale)}
                      </IonBadge>
                    </IonItem>
                  );
                })}
              </IonList>
            )}
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default Recipes;
